import * as XLSX from 'xlsx';
import { googleDriveService } from './googleDriveService';
import { productService } from './api';

class ImportService {
  // Parser un fichier Excel/CSV
  parseFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convertir en JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1,
            defval: ''
          });
          
          resolve(jsonData);
        } catch (error) {
          reject(new Error('Erreur lors de la lecture du fichier: ' + error.message));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  // Valider et nettoyer les données d'un produit
  validateProductData(row, rowIndex) {
    const errors = [];
    const product = {};

    // Mapping des colonnes basé sur le nouveau template avec TOUS les champs facets (43 colonnes)
    const columnMapping = {
      0: 'name',           // A: Nom
      1: 'sku',           // B: Référence
      2: 'description',    // C: Description
      3: 'shortDescription', // D: Description courte
      4: 'categories',     // E: Catégories (séparées par | ou ,)
      5: 'parentCategory', // F: Catégorie parent
      7: 'brand',         // H: Marque (facets.brand)
      8: 'system',        // I: Système (facets.system)
      10: 'gasType',      // K: Type de gaz (facets.gasType)
      11: 'detectorType', // L: Type de détecteurs (facets.detectorType)
      12: 'usage',        // M: Usage (facets.usage)
      13: 'connectivity', // N: Connectivité (facets.connectivity)
      14: 'functionality', // O: Fonctionnalités (facets.functionality)
      15: 'location',     // P: Localisation (facets.location)
      16: 'phase',        // Q: Phase (facets.phase)
      17: 'puissance',    // R: Puissance (facets.puissance)
      18: 'norme',        // S: Norme (facets.norme)
      19: 'solution',     // T: Solution (facets.solution)
      20: 'equipement',   // U: Équipement (facets.equipement)
      21: 'typeGaz',      // V: Type Gaz (spéc.) (facets.typeGaz)
      22: 'categorie',     // W: Catégorie (spéc.) (facets.categorie)
      23: 'type',         // X: Type (spéc.) (facets.type)
      24: 'resolution',   // Y: Résolution (facets.resolution)
      25: 'technologie',  // Z: Technologie (facets.technologie)
      26: 'debit',        // AA: Débit (facets.debit)
      27: 'fonctionnalite', // AB: Fonctionnalité (spéc.) (facets.fonctionnalite)
      28: 'typeDetecteur', // AC: Type détecteur (spéc.) (facets.typeDetecteur)
      29: 'subcategory',  // AD: Sous-catégorie (facets.subcategory)
      30: 'status',       // AE: Statut
      31: 'isVisible',    // AF: Visible
      32: 'isFeatured',   // AG: Mis en avant
      33: 'tags',         // AH: Tags
      34: 'features',     // AI: Caractéristiques
      35: 'filename',     // AJ: Nom de fichier
      36: 'imageUrl',     // AK: URL Image
      37: 'datasheetUrl', // AL: Lien Fiche Technique
      38: 'manualUrl',    // AM: Lien Manuel
      39: 'certificateUrl' // AN: Lien Certificat
    };

    // Parser chaque colonne
    Object.entries(columnMapping).forEach(([index, field]) => {
      const value = row[parseInt(index)];
      if (value !== undefined && value !== '') {
        switch (field) {
          case 'name':
            if (typeof value === 'string' && value.trim()) {
              product.name = value.trim();
            } else {
              errors.push(`Ligne ${rowIndex + 1}: Le nom du produit est requis`);
            }
            break;

          case 'location':
          case 'phase':
          case 'puissance':
          case 'norme':
          case 'solution':
          case 'equipement':
          case 'typeGaz':
          case 'categorie':
          case 'type':
          case 'resolution':
          case 'technologie':
          case 'debit':
          case 'typeDetecteur':
            product.facets = product.facets || {};
            product.facets[field] = typeof value === 'string' ? value.trim() : '';
            break;

          case 'fonctionnalite':
            product.facets = product.facets || {};
            if (typeof value === 'string' && value.trim()) {
              product.facets.fonctionnalite = value.split(',').map(v => v.trim()).filter(Boolean);
            }
            break;
          case 'datasheetUrl':
          case 'manualUrl':
          case 'certificateUrl':
            if (typeof value === 'string' && value.trim()) {
              const url = value.trim();
              product.documents = product.documents || {};
              if (field === 'datasheetUrl') product.documents.datasheet = url;
              if (field === 'manualUrl') product.documents.manual = url;
              if (field === 'certificateUrl') product.documents.certificate = url;
            }
            break;
            
          case 'description':
            product.description = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'sku':
            product.sku = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'brand':
          case 'system':
          case 'subcategory':
          case 'gasType':
          case 'detectorType':
          case 'usage':
          case 'connectivity':
            product.facets = product.facets || {};
            product.facets[field] = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'functionality':
            product.facets = product.facets || {};
            if (typeof value === 'string' && value.trim()) {
              product.facets.functionality = value.split(',').map(f => f.trim()).filter(f => f);
            }
            break;
            
          case 'categories':
            if (typeof value === 'string' && value.trim()) {
              // Accepte séparateur | ou ,
              const parts = value.split(/\||,/).map(v => v.trim()).filter(Boolean);
              if (parts.length > 0) {
                // Conserver les noms pour résolution côté backend
                product.categoryNames = parts;
                // Par compatibilité, garder la première comme 'category'
                product.category = parts[0];
              }
            }
            break;

          case 'parentCategory':
            product.parentCategory = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'subCategories':
            product.subCategories = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'category':
            product.category = typeof value === 'string' ? value.trim() : '';
            break;
            
            
          case 'price':
            // Champs prix désactivés (ignoré)
            break;
            
          case 'currency':
            // Devise désactivée (ignoré)
            break;
            
          case 'quantity':
            // Stock désactivé (ignoré)
            break;
            
          case 'unit':
            product.unit = typeof value === 'string' ? value.trim() : 'pièce';
            break;
            
          case 'weight':
            const weight = parseFloat(value);
            if (!isNaN(weight) && weight >= 0) {
              product.weight = weight;
            } else if (value !== '') {
              errors.push(`Ligne ${rowIndex + 1}: Poids invalide`);
            }
            break;
            
          case 'dimensions':
            product.dimensions = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'color':
            product.color = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'material':
            product.material = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'warranty':
            product.warranty = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'isVisible':
            const isVisible = value.toString().toLowerCase();
            product.isVisible = isVisible === 'oui' || isVisible === 'true' || isVisible === '1';
            break;
            
          case 'isFeatured':
            const isFeatured = value.toString().toLowerCase();
            product.isFeatured = isFeatured === 'oui' || isFeatured === 'true' || isFeatured === '1';
            break;
            
          case 'features':
            if (typeof value === 'string' && value.trim()) {
              product.features = value.split('|').map(f => f.trim()).filter(f => f);
            }
            break;
            
          case 'technicalSpecs':
            if (typeof value === 'string' && value.trim()) {
              product.technicalSpecs = value.split('|').map(s => s.trim()).filter(s => s);
            }
            break;
            
          case 'seoKeywords':
            if (typeof value === 'string' && value.trim()) {
              product.seoKeywords = value.split('|').map(k => k.trim()).filter(k => k);
            }
            break;
            
          case 'shortDescription':
            product.shortDescription = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'status':
            product.status = typeof value === 'string' ? value.trim() : 'Brouillon';
            break;
            
          case 'inStock':
            // En stock désactivé (ignoré)
            break;
            
          case 'tags':
            if (typeof value === 'string' && value.trim()) {
              product.tags = value.split(',').map(t => t.trim()).filter(t => t);
            }
            break;
            
          case 'filename':
            if (typeof value === 'string' && value.trim()) {
              product.filename = value.trim();
            }
            break;
            
          case 'views':
            const views = parseInt(value);
            if (!isNaN(views) && views >= 0) {
              product.views = views;
            }
            break;
            
          case 'createdAt':
            if (typeof value === 'string' && value.trim()) {
              product.createdAt = value;
            }
            break;
            
          case 'updatedAt':
            if (typeof value === 'string' && value.trim()) {
              product.updatedAt = value;
            }
            break;
            
          case 'createdBy':
            product.createdBy = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'updatedBy':
            product.updatedBy = typeof value === 'string' ? value.trim() : '';
            break;
            
          case 'imageUrl':
            if (typeof value === 'string' && value.trim()) {
              const imageUrl = value.trim();
              
              // Initialiser les tableaux si nécessaire
              product.imageUrls = product.imageUrls || [];
              product.imageUrls.push(imageUrl);
              
              // Marquer si c'est un lien Google Drive pour traitement ultérieur
              if (googleDriveService.isGoogleDriveUrl(imageUrl)) {
                product.isGoogleDriveImage = true;
                product.googleDriveUrls = product.googleDriveUrls || [];
                product.googleDriveUrls.push(imageUrl);
              }
              
              // Garder la première image comme imageUrl pour compatibilité
              if (field === 'imageUrl') {
                product.imageUrl = imageUrl;
              }
            }
            break;
        }
      }
    });

    // Vérifications obligatoires
    if (!product.name) {
      errors.push(`Ligne ${rowIndex + 1}: Le nom du produit est requis`);
    }

    // Si le produit a un nom, l'ajouter même s'il y a des erreurs mineures
    if (product.name && errors.length === 0) {
      // Ajouter des valeurs par défaut pour les champs manquants
      if (!product.pricing) {
        product.pricing = { publicPrice: null, currency: 'EUR' };
      }
      // Suppression du stock par défaut
        if (!product.facets) {
          product.facets = {};
      }
      if (!product.features) {
        product.features = [];
      }
      if (!product.technicalSpecs) {
        product.technicalSpecs = [];
      }
      if (!product.seoKeywords) {
        product.seoKeywords = [];
      }
      if (product.isVisible === undefined) {
        product.isVisible = true;
      }
      if (product.isFeatured === undefined) {
        product.isFeatured = false;
      }
    }

    return { product, errors };
  }

  // Traiter les images Google Drive - Optimisé
  async processGoogleDriveImages(products, onProgress = null) {
    const productsWithGoogleDrive = products.filter(p => p.isGoogleDriveImage);
    
    if (productsWithGoogleDrive.length === 0) {
      return products;
    }

    console.log(`🔄 Traitement optimisé de ${productsWithGoogleDrive.length} produits avec images Google Drive...`);

    // Collecter toutes les URLs Google Drive
    const allGoogleDriveUrls = [];
    const productUrlMap = new Map(); // Map pour associer les URLs aux produits

    for (let i = 0; i < productsWithGoogleDrive.length; i++) {
      const product = productsWithGoogleDrive[i];
      if (product.googleDriveUrls && product.googleDriveUrls.length > 0) {
        for (let j = 0; j < product.googleDriveUrls.length; j++) {
          const imageUrl = product.googleDriveUrls[j];
          allGoogleDriveUrls.push(imageUrl);
          if (!productUrlMap.has(imageUrl)) {
            productUrlMap.set(imageUrl, []);
          }
          productUrlMap.get(imageUrl).push({ productIndex: i, imageIndex: j });
        }
      }
    }

    console.log(`📥 Téléchargement de ${allGoogleDriveUrls.length} images Google Drive en parallèle...`);

    // Télécharger toutes les images en parallèle
    const downloadResults = await googleDriveService.downloadMultipleImages(
      allGoogleDriveUrls, 
      onProgress
    );

    // Associer les résultats aux produits
    for (let i = 0; i < downloadResults.length; i++) {
      const result = downloadResults[i];
      const originalUrl = result.originalUrl;
      const productMappings = productUrlMap.get(originalUrl);

      if (productMappings) {
        for (const mapping of productMappings) {
          const product = productsWithGoogleDrive[mapping.productIndex];
          if (!product.images) product.images = [];
          
          if (result.success) {
            // Ajouter l'image téléchargée
            product.images.push({
              main: result.filepath,
              filename: result.filename,
              originalUrl: originalUrl
            });
          } else {
            console.error(`❌ Erreur téléchargement image pour ${product.name}:`, result.error);
          }
        }
      }
    }

    return products;
  }

  // Envoyer les produits avec images au backend
  async bulkImportWithImages(products) {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    
    // Préparer les données des produits
    const productsData = [];
    
    for (let i = 0; i < products.length; i++) {
      const product = { ...products[i] };
      
      if (product.images && product.images.length > 0) {
        // Les images ont été téléchargées côté serveur, on utilise les chemins
        product.images = {
          main: product.images[0]?.path || null,
          gallery: product.images.slice(1).map(img => img.path).filter(path => path)
        };
      } else {
        product.images = {
          main: null,
          gallery: []
        };
      }
      
      productsData.push(product);
    }
    
    // Envoyer directement en JSON (plus de FormData nécessaire)
    const response = await fetch(`${API_BASE_URL}/products/bulk-import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ products: productsData })
    });
    
    if (!response.ok) {
      throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  }

  // Importer les produits
  async importProducts(file, onProgress) {
    try {
      // Parser le fichier
      const data = await this.parseFile(file);
      
      console.log('=== DEBUG IMPORT ===');
      console.log('Données parsées:', data);
      console.log('Nombre de lignes:', data.length);
      
      if (data.length < 2) {
        throw new Error('Le fichier doit contenir au moins un en-tête et une ligne de données');
      }

      // Ignorer la première ligne (en-têtes)
      const rows = data.slice(1);
      console.log('Lignes de données:', rows);
      
      const products = [];
      const allErrors = [];
      let processed = 0;

      // Traiter chaque ligne
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        
        console.log(`Ligne ${i + 1}:`, row);
        
        // Ignorer les lignes vides
        if (row.every(cell => !cell || cell.toString().trim() === '')) {
          console.log(`Ligne ${i + 1} ignorée (vide)`);
          continue;
        }

        const { product, errors } = this.validateProductData(row, i);
        
        console.log(`Produit validé ligne ${i + 1}:`, product);
        console.log(`Erreurs ligne ${i + 1}:`, errors);
        
        if (errors.length > 0) {
          allErrors.push(...errors);
        } else {
          products.push(product);
        }

        processed++;
        
        // Mettre à jour le progrès (0% → 70% pendant le parsing)
        if (onProgress) {
          const rowsProgress = Math.round((processed / rows.length) * 70);
          onProgress(rowsProgress);
        }
      }
      
      console.log('Produits finaux:', products);
      console.log('Erreurs finales:', allErrors);

      if (products.length === 0) {
        throw new Error('Aucun produit valide trouvé dans le fichier');
      }

      // Traiter les images Google Drive avant d'envoyer au backend
      const productsWithImages = await this.processGoogleDriveImages(products, (imageProgress, message) => {
        if (onProgress) {
          // Images: 70% → 90%
          const totalProgress = 70 + Math.round((imageProgress / 100) * 20);
          onProgress(totalProgress);
        }
      });

      // Envoyer les produits au backend (90% → 100%)
      if (onProgress) onProgress(95);
      const response = await this.bulkImportWithImages(productsWithImages);
      if (onProgress) onProgress(100);
      
      return {
        success: true,
        imported: response.imported || products.length,
        errors: response.errors || allErrors.length,
        details: response.details || []
      };

    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de l\'import des produits'
      };
    }
  }

  // Télécharger un modèle Excel
  downloadTemplate() {
    const templateData = [
      [
        'Nom', 'Référence', 'Description', 'Description courte', 'Catégories', 'Catégorie parent', 'Sous-catégories',
        'Marque', 'Système', 'Sous-catégorie', 'Type de gaz', 'Type de détecteurs', 'Usage', 'Connectivité', 'Fonctionnalités',
        'Statut', 'Visible', 'Mis en avant', 'Tags', 'Caractéristiques',
        'URL Image', 'Date création', 'Date modification', 'Créé par', 'Modifié par'
      ],
      [
        'Détecteur fixe SENSTRON', 'DFX-001', 'Capteur fixe pour gaz toxiques', 'Détecteur fixe',
        'detection de gaz|type de détecteurs|détecteur fixe', 'DÉTECTION DE GAZ', 'type de détecteurs|détecteur fixe',
        'SENSTRON', '', 'détecteur fixe', 'toxiques', 'fixe', 'interieur', 'filaire', '',
        'Publié', 'Oui', 'Non', '250.00', '10', 'Oui', 'gaz,capteur', 'IP65|4-20mA', '0',
        'https://example.com/detecteur.jpg', '23/09/2025', '23/09/2025', 'admin', 'admin'
      ],
      [
        'Centrale extinction AGUILERA', 'CENT-NV-01', 'Centrale pour extinction gaz NOVEC', 'Centrale extinction',
        'extinction automatique|categorie|centrale', 'EXTINCTION AUTOMATIQUE', 'categorie|centrale',
        'AGUILERA', 'NOVEC TM', 'centrale', 'NOVEC TM', '', 'interieur', 'filaire', '',
        'Publié', 'Oui', 'Non', '1990.00', '2', 'Oui', 'extinction,centrale', 'MODBUS|Relais', '0',
        'https://example.com/centrale.jpg', '23/09/2025', '23/09/2025', 'admin', 'admin'
      ],
      [
        'Module KNX ZENNIO', 'KNX-Z-01', 'Module KNX 8 sorties', 'Module KNX',
        'GRMS|CATEGORIE|MODULES KNX', 'GRMS', 'CATEGORIE|MODULES KNX',
        'ZENNIO', 'KNX', 'MODULES KNX', '', '', '', '', 'Gestion de l\'éclairage,controle du climatisation',
        'Publié', 'Oui', 'Non', 'Sur devis', '0', 'Non', 'knx,module', '8 sorties|DIN', '0', '',
        '23/09/2025', '23/09/2025', 'admin', 'admin'
      ],
      [
        'Caméra HIKVISION 4K', 'CAM-HIK-4K', 'Caméra de surveillance 4K', 'Caméra 4K',
        'videosurveillance|TYPE|caméra', 'VIDÉOSURVEILLANCE', 'TYPE|caméra',
        'HIKVISION', '4K', 'caméra', '', '', 'exterieur', 'IP', '',
        'Publié', 'Oui', 'Non', '450.00', '5', 'Oui', 'camera,surveillance', '4K|IP66|PoE', '0',
        'https://example.com/camera.jpg', '23/09/2025', '23/09/2025', 'admin', 'admin'
      ],
      [
        'Module KNX ZENNIO', 'KNX-Z-01', 'Module KNX 8 sorties', 'Module KNX',
        'grms|CATÉGORIE|MODULES KNX', 'GRMS', 'CATÉGORIE|MODULES KNX',
        'ZENNIO', '', 'MODULES KNX', '', '', '', '', 'gestion éclairage,contrôle climatisation',
        'Publié', 'Oui', 'Non', 'Sur devis', '0', 'Non', 'knx,module', '8 sorties|DIN', '0', '',
        '23/09/2025', '23/09/2025', 'admin', 'admin'
      ],
      [
        'Enceinte DSPPA', 'DSPPA-ENC-01', 'Enceinte professionnelle DSPPA', 'Enceinte DSPPA',
        'audio professionnel|TYPE|enceintes', 'AUDIO PROFESSIONNEL', 'TYPE|enceintes',
        'DSPPA', 'actives', 'enceintes', '', '', 'interieur', 'filaire', '',
        'Publié', 'Oui', 'Non', '320.00', '8', 'Oui', 'audio,enceinte', '100W|IP65', '0',
        'https://example.com/enceinte.jpg', '23/09/2025', '23/09/2025', 'admin', 'admin'
      ]
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Modèle');
    
    XLSX.writeFile(wb, 'modele-import-produits.xlsx');
  }
}

export const importService = new ImportService();
